import { createClient } from '@supabase/supabase-js'
import { createBrowserClient as createSSRBrowserClient, createServerClient as createSSRServerClient } from '@supabase/ssr'
import type { Database } from './database.types'

// supabase-js throws synchronously ("supabaseUrl/supabaseKey is required.")
// if either is empty. A dashboard page that forgets `export const dynamic =
// 'force-dynamic'` gets its client built during `next build`'s prerender
// pass, before env vars are configured, which crashed the whole build
// (tracker: Vercel build failures on MRs, 2026-08-18). Falling back to
// syntactically valid placeholders keeps client construction inert at build
// time; a real request against them fails loudly at runtime instead, which
// is the correct place to surface a genuine missing-env-var config error.
const supabaseUrl = (process.env.NEXT_PUBLIC_SUPABASE_URL ?? '').trim() || 'https://placeholder.supabase.co'
const supabaseAnonKey = (process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ?? '').trim() || 'placeholder-anon-key'
const supabaseServiceKey = (process.env.SUPABASE_SERVICE_ROLE_KEY ?? '').trim() || 'placeholder-service-key'

/** Server-side client using service role key. Bypasses RLS. Use in RSC, API routes, engine. */
export function createServerClient() {
  return createClient<Database>(supabaseUrl, supabaseServiceKey)
}

/** The project URL and service key, for the one server path that calls a Supabase REST endpoint without a client: the change feed (lib/data/v2/change-feed.ts). */
export function supabaseServiceEndpoint(): { url: string; serviceKey: string } {
  return { url: supabaseUrl, serviceKey: supabaseServiceKey }
}

/**
 * Server-side client using anon key + cookies for auth context.
 * Use in RSC pages and API routes where you need the authenticated user's session.
 * Must be called in an async context (server component or route handler).
 */
export async function createCookieClient() {
  const { cookies } = await import('next/headers')
  const cookieStore = await cookies()

  return createSSRServerClient<Database>(supabaseUrl, supabaseAnonKey, {
    cookies: {
      getAll() {
        return cookieStore.getAll()
      },
      setAll(cookiesToSet) {
        try {
          cookiesToSet.forEach(({ name, value, options }) => {
            cookieStore.set(name, value, options)
          })
        } catch {
          // setAll is called from Server Components where cookies can't be set.
          // This is fine — the middleware will refresh the session.
        }
      },
    },
  })
}

/** Client-side client using anon key with cookie-based session storage. Use in 'use client' components. */
export function createBrowserClient() {
  return createSSRBrowserClient<Database>(supabaseUrl, supabaseAnonKey)
}

/** Server-side client that reads auth from cookies. Use in middleware for session checks. */
export function createMiddlewareClient(request: Request) {
  const response = new Response(null)

  const client = createSSRServerClient<Database>(supabaseUrl, supabaseAnonKey, {
    cookies: {
      getAll() {
        const cookieHeader = request.headers.get('cookie') ?? ''
        return cookieHeader.split(';').map((c) => {
          const [name, ...rest] = c.trim().split('=')
          return { name, value: rest.join('=') }
        }).filter((c) => c.name)
      },
      setAll(cookies) {
        cookies.forEach(({ name, value, options }) => {
          const parts = [`${name}=${value}`]
          if (options?.path) parts.push(`Path=${options.path}`)
          if (options?.maxAge) parts.push(`Max-Age=${options.maxAge}`)
          if (options?.sameSite) parts.push(`SameSite=${options.sameSite}`)
          if (options?.httpOnly) parts.push('HttpOnly')
          if (options?.secure) parts.push('Secure')
          response.headers.append('Set-Cookie', parts.join('; '))
        })
      },
    },
  })

  return { client, response }
}

// ── Query kit: lives in lib/data/kernel.ts (doorway program) ───────────────
//
// The deprecated re-exports came out in slice 15c, once no importer was
// left. This module now exports ONLY the four client constructors; every
// query helper lives in '@/lib/data/kernel', and data access goes through
// a lib/data/<domain>/ repository.
